/**
 * O(1) Memory Recycling Cache for Grid Rows.
 * Prevents unnecessary recreation of row objects when their dependencies haven't changed.
 * This ensures strict reference equality (===) to assist React's virtual DOM tree in avoiding re-renders.
 */
export class RowMemoizer {
    constructor() {
        this.cache = new Map();
    }

    /**
     * Retrieves or computes a memoized row based on strict dependency equality.
     * 
     * @param {String|Number} key Unique identifier for the row (e.g., entity ID)
     * @param {Array} dependencies Array of dependency values to strictly check for changes
     * @param {Function} computeFn Function to compute the new row if cache misses
     * @returns {Object} The memoized or newly computed row
     */
    memoize(key, dependencies, computeFn) {
        const cacheKey = String(key);
        const cached = this.cache.get(cacheKey);

        // Check if all dependencies are strictly equal (===) to the cached ones
        let isCacheValid = false;
        if (cached && cached.deps.length === dependencies.length) {
            isCacheValid = cached.deps.every((dep, index) => dep === dependencies[index]);
        }

        if (isCacheValid) {
            return cached.row;
        }

        // Cache miss: compute new row and save to cache
        const newRow = computeFn();
        this.cache.set(cacheKey, {
            deps: dependencies,
            row: newRow
        });

        return newRow;
    }

    /**
     * Clears the entire memoization cache
     */
    clear() {
        this.cache.clear();
    }
}

export default RowMemoizer;
